import { z } from 'zod';
import { UsdAmount } from './ai.js';
import { FORMAT_TEXT_SAMPLE, FormatQuestion } from './chilldoc-format.js';
/**
 * Nhập nội dung từ PDF / `.docx` vào `.chilldoc` — APM-CDIMP-a,
 * docs/plan-chilldoc-import.md §4, §7.2.
 *
 * `ImportBlock` là IR trung gian: bộ đọc PDF (pdfjs, trong trình duyệt) và bộ đọc
 * `.docx` (mammoth) đều ra nó, `toDocContent` (web) biến nó thành node của
 * editor. IR **không** mang font / cỡ của nguồn — tài liệu đích áp cấu hình của
 * nó, như dán. Toạ độ `Box` là **pt, gốc trên-trái** ở mọi tầng sau `pdf-read`.
 *
 * Đường AI ("Nhập với AI", §7): tất định trước, model chỉ nhận **ảnh cắt** của
 * vùng công thức / vùng khó phân biệt và **câu hỏi có lựa chọn**; trả LaTeX
 * theo chỉ số + câu trả lời theo id. `applyImportAnswers` ghép câu trả lời vào
 * IR bằng hàm thuần — web (áp) và API (`finalize`) dùng chung.
 */
/** Số vùng ảnh tối đa gửi model một lượt; phần dư giữ dạng ảnh. */
export const IMPORT_MAX_AI_REGIONS = 20;
/**
 * Câu hỏi (cấp tiêu đề / độ sâu danh sách) gửi model mỗi lượt. Từ khi bỏ trần số trang
 * (APM-PDFTOC) một PDF dài sinh hơn thế ⇒ web chỉ gửi bấy nhiêu câu đầu, phần dư giữ mặc định.
 */
export const IMPORT_MAX_QUESTIONS = 60;
/** Số khối tóm tắt gửi model (như `PASTE_MAX_BLOCKS`). */
export const IMPORT_MAX_SUMMARY = 200;
export const IMPORT_MAX_OUTLINE = 40;
export const IMPORT_MAX_LATEX = 2000;
export const IMPORT_MAX_REGION_TEXT = 4000;
export const IMPORT_MAX_DEPTH = 4;
/**
 * Bảng kẻ khung AI đọc lại (APM-CVT-g, plan-chilldoc-convert §7.2): trần hàng × cột và độ dài
 * một ô. Vượt ⇒ `sanitizeImportResult` loại cả bảng — vùng giữ dạng ảnh, không cắt bớt (bảng cụt
 * trông như bảng đủ).
 */
export const IMPORT_MAX_TABLE_ROWS = 30;
export const IMPORT_MAX_TABLE_COLS = 12;
export const IMPORT_MAX_TABLE_CELL = 500;
/** Hộp trên trang gốc — pt, gốc trên-trái. */
export const Box = z.object({
    x: z.number(),
    y: z.number(),
    w: z.number().nonnegative(),
    h: z.number().nonnegative(),
});
/** `anchorId` của tiêu đề / đích tham chiếu chéo trong IR (APM-PDFTOC) — cùng dạng `#anchor-<id>`. */
const AnchorId = z
    .string()
    .max(64)
    .regex(/^[\w-]+$/);
export const ImportRun = z.object({
    text: z.string(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    /**
     * Run này là **tham chiếu chéo** tới tiêu đề có `anchorId` này (dòng của trang "Mục lục" in
     * sẵn, APM-PDFTOC). `text` giữ chữ của dòng để tóm tắt; `toDocContent` ra node `crossRef`
     * chỉ khi đích có trong cùng lượt nhập — không thì chữ thường, không bao giờ link chết.
     */
    refId: AnchorId.optional(),
    /**
     * Run này là một **biến số** (`PT1`, `n`, `xᵢ`) — APM-CDIMP-k: `toDocContent` ra node công thức
     * inline với LaTeX này (`PT1`, `n`, `x_{i}`) thay vì chữ nghiêng, để biến không lẫn với chữ nhấn
     * mạnh. `text` vẫn giữ chữ hiển thị — tóm tắt, tìm kiếm, `hostOffset` và `offset` câu hỏi đều đếm
     * trên nó. Bộ nhập PDF tự điền (không AI, không ảnh).
     */
    latex: z.string().max(IMPORT_MAX_LATEX).optional(),
});
export const IMPORT_LIST_FORMATS = [
    'bullet',
    'decimal',
    'lower-alpha',
    'upper-alpha',
    'lower-roman',
    'upper-roman',
];
const Page = z.number().int().nonnegative();
const Level = z.number().int().min(1).max(4);
const Depth = z.number().int().min(0).max(IMPORT_MAX_DEPTH);
const HeadingBlock = z.object({
    kind: z.literal('heading'),
    level: Level,
    runs: z.array(ImportRun),
    page: Page,
    /** Id đích tham chiếu chéo (`anchorId` của node heading) — có khi dàn ý dựng từ mục lục PDF trỏ tới. */
    anchorId: AnchorId.optional(),
});
const ParagraphBlock = z.object({
    kind: z.literal('paragraph'),
    runs: z.array(ImportRun),
    /** Thụt lề trái / dòng đầu (mm) — vào `indentLeft` / `indentFirst`. */
    indentMm: z.number().nonnegative().optional(),
    firstLineMm: z.number().optional(),
    /** `.chilldoc` chưa có căn đoạn — giữ để sau; `toDocContent` hiện bỏ qua. */
    align: z.enum(['left', 'center', 'right']).optional(),
    page: Page,
});
const ListItemBlock = z.object({
    kind: z.literal('listItem'),
    ordered: z.boolean(),
    depth: Depth,
    /** Dấu đọc được từ nguồn (`1.`, `a)`, `•`) — chỉ để hiển thị / debug; kiểu đánh số là cấu hình tài liệu đích. */
    marker: z.string().max(16),
    format: z.enum(IMPORT_LIST_FORMATS).optional(),
    runs: z.array(ImportRun),
    page: Page,
});
const FigureBlock = z.object({
    kind: z.literal('figure'),
    /** Id trong kho ảnh nhúng (`embedded-images`). */
    imageId: z.string(),
    /** % bề ngang cột chữ của nguồn — quy ra px theo vùng chữ của tài liệu đích. */
    widthPct: z.number().min(10).max(100),
    caption: z.array(ImportRun).optional(),
    /** Vùng mực nhiều chữ (≥ 40 % diện tích là hộp chữ) hoặc trang scan — "Nhập với AI" đưa model phân xử hình / chữ. */
    ambiguous: z.boolean().optional(),
    /**
     * Vùng có lưới kẻ (≥ 2 đường ngang + ≥ 2 đường dọc chạy hết vùng — `pdf-regions`): có thể là
     * bảng kẻ khung đang thành ảnh; "Dùng AI" đưa model đọc thành hàng × ô (APM-CVT-g).
     */
    ruled: z.boolean().optional(),
    page: Page,
    region: Box,
});
const FormulaBlock = z.object({
    kind: z.literal('formula'),
    imageId: z.string(),
    display: z.enum(['inline', 'block']),
    page: Page,
    region: Box,
    /** `inline`: thuộc khối nào (chỉ số trong mảng) và chèn ở ký tự thứ mấy của chữ khối ấy. */
    hostIndex: z.number().int().nonnegative().optional(),
    hostOffset: z.number().int().nonnegative().optional(),
    /** Đường AI điền vào; có thì `toDocContent` ra node công thức thay vì ảnh. */
    latex: z.string().max(IMPORT_MAX_LATEX).optional(),
});
const TableBlock = z.object({
    kind: z.literal('table'),
    /** `rows[r][c]` là các run của một ô. */
    rows: z.array(z.array(z.array(ImportRun))),
    headerRows: z.number().int().min(0).max(5),
    /** Chú thích ("Bảng 1. …") của hình kẻ khung mà AI đọc thành bảng — đoạn nghiêng ngay sau bảng. */
    caption: z.array(ImportRun).optional(),
    page: Page,
});
/** Trần chữ một khối mã nhập từ PDF — một trang A4 mã dày cỡ ~5 000 ký tự. */
export const IMPORT_MAX_CODE = 20_000;
/**
 * Khối mã nguồn (APM-CDIMP-n) — mỗi dòng nguồn một dòng, thụt lề bằng dấu cách ở đầu dòng, `\n` ngăn dòng (dòng
 * trống giữa hai hàm giữ nguyên). Vào node `codeBlock` của `.chilldoc`; không mark, không công thức.
 */
const CodeBlock = z.object({
    kind: z.literal('code'),
    text: z.string().max(IMPORT_MAX_CODE),
    /** Ngôn ngữ đoán được (`c`) — vắng ⇒ khối mã không tô màu. */
    language: z.string().max(32).optional(),
    page: Page,
});
export const IMPORT_UNSUPPORTED = ['equation', 'chart', 'object'];
const UnsupportedBlock = z.object({
    kind: z.literal('unsupported'),
    what: z.enum(IMPORT_UNSUPPORTED),
    page: Page,
});
export const ImportBlock = z.discriminatedUnion('kind', [
    HeadingBlock,
    ParagraphBlock,
    ListItemBlock,
    FigureBlock,
    FormulaBlock,
    TableBlock,
    CodeBlock,
    UnsupportedBlock,
]);
/** Chữ thuần của một khối — để tóm tắt và để tính `hostOffset`. */
export function importBlockText(block) {
    switch (block.kind) {
        case 'heading':
        case 'paragraph':
        case 'listItem':
            return block.runs.map((run) => run.text).join('');
        case 'figure':
            return (block.caption ?? []).map((run) => run.text).join('');
        case 'table':
            return block.rows
                .map((row) => row.map((cell) => cell.map((run) => run.text).join('')).join(' | '))
                .join('\n');
        case 'code':
            return block.text;
        default:
            return '';
    }
}
/**
 * Một khối rút gọn cho model — cùng vai với `PasteBlockSummary`. Không có chữ
 * nguyên văn: chỉ `FORMAT_TEXT_SAMPLE` ký tự đầu.
 */
export const ImportBlockSummary = z.object({
    index: z.number().int().nonnegative(),
    kind: z.enum(['heading', 'paragraph', 'listItem', 'figure', 'formula', 'table', 'code', 'unsupported']),
    level: Level.optional(),
    depth: Depth.optional(),
    ordered: z.boolean().optional(),
    text: z.string().max(FORMAT_TEXT_SAMPLE),
    page: Page,
});
export function summarizeImportBlocks(blocks) {
    return blocks.slice(0, IMPORT_MAX_SUMMARY).map((block, index) => ({
        index,
        kind: block.kind,
        ...(block.kind === 'heading' ? { level: block.level } : {}),
        ...(block.kind === 'listItem' ? { depth: block.depth, ordered: block.ordered } : {}),
        text: importBlockText(block).slice(0, FORMAT_TEXT_SAMPLE),
        page: block.page,
    }));
}
// ---------------------------------------------------------------------------
// Đường AI — §7.2
// ---------------------------------------------------------------------------
/**
 * Một vùng ảnh gửi model: công thức cần LaTeX, vùng mực nhiều chữ chưa rõ là hình hay đoạn chữ,
 * hoặc hình có lưới kẻ có thể là bảng (`table`, APM-CVT-g).
 */
export const ImportAiRegion = z.object({
    /** Chỉ số khối trong IR. */
    index: z.number().int().nonnegative(),
    kind: z.enum(['formula', 'ambiguous', 'table']),
    /** Bản thu nhỏ đã PUT lên S3 scratch (`POST /me/files/image-description/upload`). */
    scratchId: z.uuid(),
    display: z.enum(['inline', 'block']).optional(),
    contextBefore: z.string().max(FORMAT_TEXT_SAMPLE).optional(),
    contextAfter: z.string().max(FORMAT_TEXT_SAMPLE).optional(),
    page: Page,
});
export const ImportAiInput = z.object({
    regions: z.array(ImportAiRegion).max(IMPORT_MAX_AI_REGIONS),
    questions: z.array(FormatQuestion).max(IMPORT_MAX_QUESTIONS),
    /** Dàn ý của tài liệu ĐÍCH quanh chỗ chèn — để cấp tiêu đề tương đối đúng chỗ. */
    outline: z
        .array(z.object({ level: z.number().int().min(1).max(4), text: z.string().max(FORMAT_TEXT_SAMPLE) }))
        .max(IMPORT_MAX_OUTLINE),
    summary: z.array(ImportBlockSummary).max(IMPORT_MAX_SUMMARY),
    runId: z.uuid().optional(),
});
/**
 * Lệnh TeX không bao giờ cần cho một công thức hiển thị — MathLive không chạy
 * chúng, nhưng không có lý do để lưu một chuỗi như thế vào tệp của người dùng.
 */
export const LATEX_FORBIDDEN = /\\(input|include|write|immediate|csname|catcode|openout|read)\b/;
export const ImportFormulaResult = z.object({
    index: z.number().int().nonnegative(),
    /** `null` = model không chắc ⇒ giữ ảnh. */
    latex: z.string().trim().max(IMPORT_MAX_LATEX).nullable(),
    display: z.enum(['inline', 'block']),
});
export const ImportRegionResult = z.object({
    index: z.number().int().nonnegative(),
    /** Vùng `ambiguous`: `figure` | `text`. Vùng `table`: `figure` (giữ ảnh) | `table`. */
    as: z.enum(['figure', 'text', 'table']),
    /** Chỉ khi `as: 'text'`: chữ đọc được trong vùng. */
    text: z.string().max(IMPORT_MAX_REGION_TEXT).optional(),
    /**
     * Chỉ khi `as: 'table'`: `rows[r][c]` là chữ của một ô, hàng đầu là tiêu đề. Schema chỉ chặn
     * cỡ thô (để model nộp quá trần còn nhận được lý do); trần thật
     * (`IMPORT_MAX_TABLE_ROWS` × `IMPORT_MAX_TABLE_COLS`, ô ≤ `IMPORT_MAX_TABLE_CELL`) ở
     * `sanitizeImportResult`.
     */
    rows: z
        .array(z.array(z.string().max(IMPORT_MAX_TABLE_CELL * 4)).max(IMPORT_MAX_TABLE_COLS * 4))
        .max(IMPORT_MAX_TABLE_ROWS * 4)
        .optional(),
});
const ImportAnswer = z.object({
    id: z.string().trim().min(1).max(40),
    choice: z.string().trim().min(1).max(40),
    why: z.string().trim().max(120).optional(),
});
export const ImportAiResult = z.object({
    formulas: z.array(ImportFormulaResult).max(IMPORT_MAX_AI_REGIONS),
    regions: z.array(ImportRegionResult).max(IMPORT_MAX_AI_REGIONS),
    answers: z.array(ImportAnswer).max(60),
    notes: z.array(z.string().trim().max(200)).max(3),
});
/** Phản hồi của `POST /me/files/import-plan` — cùng khuôn `FormatDocumentResult`. */
export const ImportAiPlanResult = z.object({
    result: ImportAiResult,
    /** Chuỗi thập phân như mọi số tiền AI khác. `'0'` khi không có lượt LLM. */
    costUsd: UsdAmount,
    partial: z.boolean(),
    runId: z.uuid().optional(),
});
/**
 * Lọc kết quả của model về đúng những gì được hỏi — dùng ở `finalize` (API) và
 * trước khi áp (web). Trả về phần hợp lệ + lý do từng mục bị loại.
 */
export function sanitizeImportResult(input, result) {
    const rejected = [];
    const regionByIndex = new Map(input.regions.map((region) => [region.index, region]));
    const questionById = new Map(input.questions.map((question) => [question.id, question]));
    const formulas = result.formulas.filter((formula) => {
        const region = regionByIndex.get(formula.index);
        if (!region || region.kind !== 'formula') {
            rejected.push(`formula ${formula.index}: không có vùng công thức này`);
            return false;
        }
        if (formula.latex !== null && LATEX_FORBIDDEN.test(formula.latex)) {
            rejected.push(`formula ${formula.index}: LaTeX chứa lệnh không được phép`);
            return false;
        }
        if (formula.latex !== null && formula.latex.length === 0) {
            rejected.push(`formula ${formula.index}: LaTeX rỗng`);
            return false;
        }
        return true;
    });
    const regions = [];
    for (const region of result.regions) {
        const source = regionByIndex.get(region.index);
        if (!source || source.kind === 'formula') {
            rejected.push(`region ${region.index}: không phải vùng cần phân xử`);
            continue;
        }
        if (source.kind === 'ambiguous' && region.as === 'table') {
            rejected.push(`region ${region.index}: vùng chưa rõ chỉ nhận "figure" hoặc "text"`);
            continue;
        }
        if (source.kind === 'table' && region.as === 'text') {
            rejected.push(`region ${region.index}: vùng bảng chỉ nhận "figure" hoặc "table"`);
            continue;
        }
        if (region.as === 'text' && !region.text?.trim()) {
            rejected.push(`region ${region.index}: chọn "chữ" mà không có chữ`);
            continue;
        }
        if (region.as === 'table') {
            const checked = checkTableRows(region.rows ?? []);
            if (typeof checked === 'string') {
                rejected.push(`region ${region.index}: ${checked}`);
                continue;
            }
            regions.push({ index: region.index, as: 'table', rows: checked });
            continue;
        }
        regions.push(region);
    }
    const answers = result.answers.filter((answer) => {
        const question = questionById.get(answer.id);
        if (!question) {
            rejected.push(`answer ${answer.id}: không có câu hỏi này`);
            return false;
        }
        if (!question.options.includes(answer.choice)) {
            rejected.push(`answer ${answer.id}: "${answer.choice}" không nằm trong lựa chọn`);
            return false;
        }
        return true;
    });
    return { result: { ...result, formulas, regions, answers }, rejected };
}
/**
 * Bảng model đọc ⇒ bảng vuông đã cắt khoảng trắng, hoặc lý do loại. Hàng / cột rỗng ở **cuối**
 * bỏ đi (model hay đệm); còn lại vượt trần ⇒ loại cả bảng (không cắt — bảng cụt trông như bảng đủ).
 */
function checkTableRows(raw) {
    const rows = raw.map((row) => row.map((cell) => cell.trim()));
    while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === ''))
        rows.pop();
    let cols = 0;
    for (const row of rows) {
        let width = row.length;
        while (width > 0 && row[width - 1] === '')
            width -= 1;
        cols = Math.max(cols, width);
    }
    if (rows.length === 0 || cols === 0)
        return 'bảng rỗng';
    if (rows.length > IMPORT_MAX_TABLE_ROWS) {
        return `bảng ${rows.length} hàng vượt trần ${IMPORT_MAX_TABLE_ROWS} — giữ "figure"`;
    }
    if (cols > IMPORT_MAX_TABLE_COLS) {
        return `bảng ${cols} cột vượt trần ${IMPORT_MAX_TABLE_COLS} — giữ "figure"`;
    }
    if (rows.some((row) => row.some((cell) => cell.length > IMPORT_MAX_TABLE_CELL))) {
        return `có ô dài quá ${IMPORT_MAX_TABLE_CELL} ký tự — giữ "figure"`;
    }
    return rows.map((row) => Array.from({ length: cols }, (_, c) => row[c] ?? ''));
}
/**
 * Thay từ `from` bằng `to` trong chuỗi run: ở `offset` nếu đúng chỗ, không thì ở lần xuất
 * hiện **duy nhất** của `from`; từ vắt qua nhiều run thì chữ mới vào run đầu. `null` = không thay.
 */
export function replaceWordInRuns(runs, from, to, offset) {
    const text = runs.map((run) => run.text).join('');
    let at = offset !== undefined && text.slice(offset, offset + from.length) === from ? offset : -1;
    if (at < 0) {
        const first = text.indexOf(from);
        if (first < 0 || text.indexOf(from, first + 1) >= 0)
            return null;
        at = first;
    }
    const end = at + from.length;
    const out = [];
    let cursor = 0;
    let placed = false;
    for (const run of runs) {
        const start = cursor;
        const stop = cursor + run.text.length;
        cursor = stop;
        if (stop <= at || start >= end) {
            out.push({ ...run });
            continue;
        }
        const before = run.text.slice(0, Math.max(0, at - start));
        const after = run.text.slice(Math.min(run.text.length, end - start));
        const middle = placed ? '' : to;
        placed = true;
        const replaced = before + middle + after;
        if (replaced)
            out.push({ ...run, text: replaced });
    }
    return out;
}
/**
 * Ghép kết quả AI vào IR — hàm thuần, không đổi đầu vào.
 *
 * - `formulas`: `latex` vào khối `formula` cùng chỉ số (giữ ảnh khi `null`).
 * - `regions`: vùng `ambiguous` chọn `text` ⇒ khối `figure` thành `paragraph`
 *   mang chữ model đọc (một ngoại lệ có chủ đích của "không bịa chữ": người
 *   dùng chọn đường AI để đọc được vùng này). Vùng `table` chọn `table` ⇒ khối `figure`
 *   thành `table { rows, headerRows: 1 }`, chú thích của hình theo sang bảng (APM-CVT-g).
 * - `answers`: `heading` (`h1`…`h4` | `paragraph`) đổi đoạn ↔ tiêu đề;
 *   `listDepth` (`0`…`4`) đổi độ sâu mục; `level` (`sibling` | `child`) dịch
 *   mọi tiêu đề của IR một cấp xuống khi `child`; `hyphen` thay từ ở `offset`
 *   (`toplevel` ⇒ `top-level`) — lệch `offset` (đoạn bị tách / rút ký tự giữ chỗ)
 *   thì tìm từ ấy trong khối, chỉ thay khi xuất hiện đúng một lần (APM-CDIMP-j).
 *
 * Câu trả lời ngoài `options` đã bị `sanitizeImportResult` loại; ở đây tin đầu vào.
 */
export function applyImportAnswers(blocks, input, result, answers = result.answers) {
    const out = blocks.map((block) => ({ ...block }));
    for (const formula of result.formulas) {
        const block = out[formula.index];
        if (block?.kind !== 'formula' || formula.latex === null)
            continue;
        out[formula.index] = { ...block, latex: formula.latex, display: formula.display };
    }
    for (const region of result.regions) {
        const block = out[region.index];
        if (block?.kind !== 'figure')
            continue;
        if (region.as === 'text' && region.text) {
            out[region.index] = {
                kind: 'paragraph',
                runs: [{ text: region.text.trim() }],
                page: block.page,
            };
        }
        else if (region.as === 'table' && region.rows && region.rows.length > 0) {
            out[region.index] = {
                kind: 'table',
                rows: region.rows.map((row) => row.map((cell) => (cell ? [{ text: cell }] : []))),
                headerRows: 1,
                ...(block.caption ? { caption: block.caption } : {}),
                page: block.page,
            };
        }
    }
    const byId = new Map(answers.map((answer) => [answer.id, answer.choice]));
    // Gạch nối: áp theo `offset` giảm dần trong mỗi khối để lần thay trước không làm lệch lần sau.
    const hyphenQuestions = input.questions
        .filter((question) => question.kind === 'hyphen' && question.index !== undefined)
        .sort((a, b) => (b.offset ?? 0) - (a.offset ?? 0));
    for (const question of hyphenQuestions) {
        const choice = byId.get(question.id);
        const from = question.default;
        if (choice === undefined || choice === from || !question.options.includes(choice))
            continue;
        const block = out[question.index];
        if (!block || (block.kind !== 'paragraph' && block.kind !== 'heading' && block.kind !== 'listItem'))
            continue;
        const runs = replaceWordInRuns(block.runs, from, choice, question.offset);
        if (runs)
            out[question.index] = { ...block, runs };
    }
    let shiftHeadings = 0;
    for (const question of input.questions) {
        const choice = byId.get(question.id);
        if (choice === undefined || !question.options.includes(choice))
            continue;
        const block = question.index === undefined ? undefined : out[question.index];
        switch (question.kind) {
            case 'heading': {
                if (!block || (block.kind !== 'paragraph' && block.kind !== 'heading'))
                    break;
                if (choice === 'paragraph') {
                    out[question.index] = { kind: 'paragraph', runs: block.runs, page: block.page };
                }
                else {
                    const level = Number(choice.replace(/^h/, ''));
                    if (level >= 1 && level <= 4) {
                        out[question.index] = {
                            kind: 'heading',
                            level: level,
                            runs: block.runs,
                            page: block.page,
                            // Đích của tham chiếu chéo (mục lục PDF) đi theo tiêu đề khi đổi cấp.
                            ...(block.kind === 'heading' && block.anchorId ? { anchorId: block.anchorId } : {}),
                        };
                    }
                }
                break;
            }
            case 'listDepth': {
                if (block?.kind !== 'listItem')
                    break;
                const depth = Number.parseInt(choice, 10);
                if (depth >= 0 && depth <= IMPORT_MAX_DEPTH)
                    out[question.index] = { ...block, depth };
                break;
            }
            case 'level':
                if (choice === 'child')
                    shiftHeadings = 1;
                break;
            default:
                break;
        }
    }
    if (shiftHeadings) {
        for (let i = 0; i < out.length; i += 1) {
            const block = out[i];
            if (block.kind === 'heading') {
                out[i] = { ...block, level: Math.min(4, block.level + shiftHeadings) };
            }
        }
    }
    return out;
}
//# sourceMappingURL=chilldoc-import.js.map